import React from 'react';
import PropTypes from 'prop-types';
import { Typography, Icon, ButtonBase } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { JOB_TASKS_STATUSES } from 'appConstants';
import classNames from 'classnames';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  button: {
    display: 'inline-block',
    cursor: 'pointer',
    border: 'none',
    padding: 0,
    color: theme.palette.text.grey,
    background: 'none',
    whiteSpace: 'nowrap',
  },
  text: {
    color: 'currentColor',
    verticalAlign: 'middle',
    fontSize: '14px',
    lineHeight: '16px',
    textTransform: 'lowercase',
  },
  icon: {
    color: 'currentColor',
    fontSize: '17px',
    marginRight: '1vw',
    verticalAlign: 'middle',
  },
  'color-mixed': {
    color: '#D662C0',
  },
  'color-performed': {
    color: theme.palette.primary.main,
  },
  inlineLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  inlineHint: {
    fontSize: '12px',
    lineHeight: '14px',
    color: theme.palette.text.grey,
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: '8px',
    padding: '0 4px',
  },
  eligibleCount: {
    color: '#2e7d32',
    fontWeight: 600,
  },
  notEligibleCount: {
    color: theme.palette.error.main,
    fontWeight: 600,
  },
  totalCount: {
    color: theme.palette.text.grey,
    fontWeight: 500,
  },
  separator: {
    color: theme.palette.text.grey,
    margin: '0 4px',
  },
}));

const parseNonNegativeInt = value => (Number.isInteger(value) && value >= 0 ? value : null);

const MultipleStateToggler = ({ value, onChange, summary, ...otherProps }) => {
  const classes = useStyles();

  const button = classNames({
    [classes.button]: true,
    [classes['color-mixed']]: value === JOB_TASKS_STATUSES.mixed,
    [classes['color-performed']]: value === JOB_TASKS_STATUSES.performed,
  });
  const totalCount = parseNonNegativeInt(summary?.totalJobTasksCount);
  const notEligibleCount = parseNonNegativeInt(summary?.notEligibleCount ?? summary?.notPerformedCount);
  const eligibleCountFromSummary = parseNonNegativeInt(summary?.eligibleOrPerformedCount ?? summary?.performedCount);
  const eligibleCount =
    eligibleCountFromSummary !== null
      ? eligibleCountFromSummary
      : totalCount !== null && notEligibleCount !== null
      ? totalCount - notEligibleCount
      : null;
  const hasSummary =
    totalCount !== null &&
    notEligibleCount !== null &&
    eligibleCount !== null &&
    eligibleCount <= totalCount &&
    notEligibleCount <= totalCount;
  const label = (
    <span className={classes.inlineLabel}>
      <span>{value}</span>
      {hasSummary && (
        <span className={classes.inlineHint}>
          <span className={classes.eligibleCount}>{eligibleCount}</span>
          <span className={classes.separator}>/</span>
          <span className={classes.notEligibleCount}>{notEligibleCount}</span>
          <span className={classes.separator}>/</span>
          <span className={classes.totalCount}>{totalCount}</span>
        </span>
      )}
    </span>
  );

  return (
    <div className={classes.root}>
      <ButtonBase
        className={button}
        type="button"
        data-key={otherProps.id}
        data-val={value}
        aria-label="state"
        onClick={onChange}
      >
        <Icon className={classes.icon}>lens</Icon>
        <Typography className={classes.text} component="span" variant="body1">
          {label}
        </Typography>
      </ButtonBase>
    </div>
  );
};

MultipleStateToggler.defaultProps = {
  onChange: () => {},
  value: undefined,
  summary: null,
};

MultipleStateToggler.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  summary: PropTypes.shape({
    eligibleOrPerformedCount: PropTypes.number,
    notEligibleCount: PropTypes.number,
    performedCount: PropTypes.number,
    notPerformedCount: PropTypes.number,
    totalJobTasksCount: PropTypes.number,
    notPerformedJobTasks: PropTypes.arrayOf(PropTypes.string),
  }),
};

export default MultipleStateToggler;
